import React, { useEffect, useMemo, useState } from "react"
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogContent,
	Grid,
	IconButton,
	InputAdornment,
	TextField,
	ThemeProvider,
	Tooltip,
	Typography
} from "@material-ui/core"
import {
	Add as AddIcon,
	Close as CloseIcon,
	Search as SearchIcon,
	Visibility as VisibilityIcon
} from "@material-ui/icons"

import { ChatBotFlowTriggerType } from "@/protocols/chatBot"
import { WebhookTemplateType } from "@/protocols/webhook"
import ChatBotFlowService, { CategorizedChatBotFlows } from "@/services/ChatBotFlow"

import ApiService from "@/services/Api"
import ErrorHandlerService from "@/services/ErrorHandler"
import { ErrorType } from "@/hooks/useValidation"

import FlowPreview from "@/pages/Admin/Flow/FlowConstructor/FlowPreview"
import { Notification } from "@/components"
import { ArrowUpRight } from "lucide-react"

import { colors } from "@/styles/colors"
import theme from "@/styles/theme"
import useDidMount from "@/hooks/useDidMount"
import useStyles from "@/pages/Admin/Flow/ChatBotCreation/ChatBotTemplate/styles"
import { normalizeSearchTerm } from "@/utils/format"
import clsx from "clsx"

type ChatBotTemplate = {
	id: number
	name: string
	description?: string
	category?: ChatBotFlowTriggerType | "no-triggers"
	webhook_template?: {
		type: WebhookTemplateType | "custom"
		display_title?: string
	}
}

type ChatBotTemplatePickerProps = {
	open: boolean
	onClose: () => void
	handleGetChatBotList?: () => Promise<void>
	redirectToFlow: (flowId: number) => void
}

const ChatBotTemplatePicker: React.FC<ChatBotTemplatePickerProps> = (props) => {
	const { open, onClose, handleGetChatBotList, redirectToFlow } = props
	const classes = useStyles()

	const [search, setSearch] = useState("")
	const [templates, setTemplates] = useState<ChatBotTemplate[]>([])
	const [isLoadingTemplates, setIsLoadingTemplates] = useState(true)
	const [existingBotNames, setExistingBotNames] = useState<string[]>([])
	const [isCreatingFromScratch, setIsCreatingFromScratch] = useState(false)
	const [creatingTemplateId, setCreatingTemplateId] = useState<number | null>(null)
	const [viewingTemplateId, setViewingTemplateId] = useState<number | null>(null)

	const isCreating = isCreatingFromScratch || creatingTemplateId !== null

	const fetchTemplates = async () => {
		try {
			const response = await ApiService.get("/chat-bot/templates", { params: { type: "flow" } })
			setTemplates(response.data.chatBotFlows ?? [])
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
			Notification.error({ message: "Não foi possível listar os templates." })
		}
	}

	const fetchExistingBotNames = async () => {
		try {
			const categorized: CategorizedChatBotFlows = await ChatBotFlowService.getAllCategorized("flow")
			const names: string[] = []
			Object.values(categorized).forEach(group => {
				group.chat_bot_flow_in_chat_bot_triggers.forEach(bot => names.push(bot.name))
			})
			setExistingBotNames(names)
		} catch {
			Notification.error({ message: "Não foi possível listar os bots existentes." })
		}
	}

	const fetchData = async () => {
		setIsLoadingTemplates(true)
		await Promise.all([fetchTemplates(), fetchExistingBotNames()])
		setIsLoadingTemplates(false)
	}

	useDidMount(() => {
		fetchData()
	})

	useEffect(() => {
		if (open) {
			setSearch("")
			fetchData()
		}
	}, [open])

	const filteredTemplates = useMemo(() => {
		const searchTerm = normalizeSearchTerm(search)
		if (!searchTerm) return templates

		return templates.filter(template =>
			normalizeSearchTerm(template.name).includes(searchTerm) ||
			(template.description ? normalizeSearchTerm(template.description).includes(searchTerm) : false)
		)
	}, [templates, search])

	const getNextBotName = (): string => {
		const base = "Novo Bot"
		const candidates = [base, ...Array.from({ length: existingBotNames.length }, (_, i) => `${base} ${i + 1}`)]
		return candidates.find(name => !existingBotNames.includes(name)) ?? base
	}

	const handleCreateFromScratch = async () => {
		if (isCreating) return
		setIsCreatingFromScratch(true)

		try {
			const name = getNextBotName()
			const chatBotFlow = await ChatBotFlowService.create("flow", { name }, "chat-bot")
			await handleGetChatBotList?.()
			onClose()
			redirectToFlow(chatBotFlow.id)
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
			Notification.error({ message: "Não foi possível criar o bot." })
		}

		setIsCreatingFromScratch(false)
	}

	const handleUseTemplate = async (templateId: number) => {
		if (isCreating) return
		setCreatingTemplateId(templateId)

		try {
			const response = await ApiService.post("/chat-bot/templates", { templateId })
			await handleGetChatBotList?.()
			onClose()
			Notification.success({ message: "Bot criado com sucesso!" })
			redirectToFlow(response.data.id)
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
			Notification.error({ message: "Não foi possível criar o bot a partir desse template." })
		}

		setCreatingTemplateId(null)
	}

	const handlePreviewTemplate = async (event: React.MouseEvent, templateId: number) => {
		event.stopPropagation()
		setViewingTemplateId(templateId)

		try {
			const response = await ApiService.get(`/chat-bot/templates/${templateId}`)
			FlowPreview.open({
				flowData: response.data.template,
				onSave: async () => handleUseTemplate(templateId)
			})
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
			Notification.error({ message: "Não foi possível visualizar o template." })
		} finally {
			setViewingTemplateId(null)
		}
	}

	return (
		<ThemeProvider theme={theme}>
			<Dialog
				open={open}
				onClose={isCreating ? undefined : onClose}
				disableBackdropClick={isCreating}
				PaperProps={{ className: classes.paper }}
			>
				<Box className={classes.header}>
					<Typography className={classes.title}>
						Novo bot
					</Typography>
					<IconButton
						onClick={onClose}
						disabled={isCreating}
						size="small"
						className={classes.closeButton}
					>
						<CloseIcon fontSize="small" />
					</IconButton>
				</Box>

				<DialogContent className={classes.body}>
					<TextField
						fullWidth
						variant="outlined"
						placeholder="Pesquisar por nome ou descrição do template"
						value={search}
						onChange={event => setSearch(event.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon className={classes.searchIcon} />
								</InputAdornment>
							),
							classes: {
								root: classes.searchInputRoot,
								notchedOutline: classes.searchInputOutline
							}
						}}
						inputProps={{ className: classes.searchInputInner }}
					/>

					<Box
						className={classes.heroCard}
						onClick={handleCreateFromScratch}
						role="button"
						tabIndex={0}
						onKeyPress={event => event.key === "Enter" && handleCreateFromScratch()}
					>
						<Box className={classes.heroIcon}>
							{isCreatingFromScratch
								? <CircularProgress size={22} style={{ color: colors.purple[500] }} />
								: <AddIcon htmlColor={colors.purple[500]} style={{ fontSize: 24 }} />
							}
						</Box>

						<Box className={classes.heroText}>
							<Typography className={classes.heroTitle}>Começar do zero</Typography>
							<Typography className={classes.heroSubtitle}>
								Crie um bot em branco e monte o fluxo do seu jeito, sem partir de um modelo.
							</Typography>
						</Box>

						<Box className={classes.heroFooter}>
							<Button
								className={classes.darkPill}
								onClick={event => { event.stopPropagation(); handleCreateFromScratch() }}
								disabled={isCreating}
								disableRipple
								disableElevation
							>
								{isCreatingFromScratch
									? <CircularProgress size={14} style={{ color: colors.gray[800] }} />
									: "Criar do zero"
								}

								<ArrowUpRight size={20} style={{ marginLeft: 4 }} />
							</Button>
						</Box>
					</Box>

					<Box>
						<Box className={classes.sectionLabel}>
							<Typography className={classes.sectionTitle}>Criar bot a partir de um template</Typography>
							{!isLoadingTemplates && (
								<Typography className={classes.sectionCount}>
									{filteredTemplates.length}
								</Typography>
							)}
						</Box>

						{isLoadingTemplates ? (
							<Grid container justifyContent="center" className={classes.loadingContainer}>
								<CircularProgress size={32} style={{ color: colors.purple[500] }} />
							</Grid>
						) : filteredTemplates.length === 0 ? (
							<Typography className={classes.emptyText}>
								Nenhum template encontrado.
							</Typography>
						) : (
							<Box className={classes.templateGrid}>
								{filteredTemplates.map(chatBotTemplate => (
									<Box
										key={chatBotTemplate.id}
										className={classes.libCard}
										onClick={() => handleUseTemplate(chatBotTemplate.id)}
										role="button"
										tabIndex={0}
										onKeyPress={event => event.key === "Enter" && handleUseTemplate(chatBotTemplate.id)}
									>
										<Tooltip title={chatBotTemplate.name} placement="top">
											<Typography className={classes.libName}>
												{chatBotTemplate.name}
											</Typography>
										</Tooltip>

										{chatBotTemplate.description && (
											<Typography className={classes.libDesc}>
												{chatBotTemplate.description}
											</Typography>
										)}

										<Box className={classes.libFooter}>
											<Button
												className={clsx(classes.darkPill, classes.cardUseTemplateButton)}
												onClick={event => { event.stopPropagation(); handleUseTemplate(chatBotTemplate.id) }}
												disabled={isCreating}
												disableRipple
												disableElevation
											>
												{creatingTemplateId === chatBotTemplate.id
													? <CircularProgress size={14} style={{ color: colors.gray[0] }} />
													: "Usar template"
												}
											</Button>

											<Tooltip title="Visualizar" placement="top">
												<IconButton
													className={classes.ghostCircleButton}
													onClick={event => handlePreviewTemplate(event, chatBotTemplate.id)}
													disabled={isCreating}
													disableRipple
													aria-label="Visualizar template"
												>
													{
														viewingTemplateId === chatBotTemplate.id ? (
															<CircularProgress size={16} style={{ color: colors.gray[500] }} />
														) : (
															<VisibilityIcon htmlColor={colors.gray[500]} style={{ fontSize: 18 }} />
														)
													}
												</IconButton>
											</Tooltip>
										</Box>
									</Box>
								))}
							</Box>
						)}
					</Box>

				</DialogContent>
			</Dialog>
		</ThemeProvider>
	)
}

export default ChatBotTemplatePicker
